import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import RoleSwitcher from './RoleSwitcher';

const Sidebar = ({ isMobileMenuOpen, closeMobileMenu, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [currentRole, setCurrentRole] = useState('learner');

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
    } else {
      // Clear user profile when user logs out
      setUserProfile(null);
      setCurrentRole('learner');
    }
  }, [currentUser]);

  const fetchUserProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        setUserProfile(profileData);
        setCurrentRole(profileData.activeRole || profileData.userType || 'learner');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // General navigation links (available to all users)
  const generalLinks = [
    {
      path: '/',
      label: 'Home',
      icon: '🏠',
      requiresAuth: false
    },
    {
      path: '/providers',
      label: 'Find a Provider',
      icon: '🔍',
      requiresAuth: false
    }
  ];

  const practitionerLinks = [
    {
      path: '/practitioner-profile',
      label: 'My Practitioner Profile',
      icon: '👤',
      requiresAuth: true
    },
    {
      path: '/practitioner-sessions',
      label: 'My Practitioner Sessions',
      icon: '📅',
      requiresAuth: true
    }
  ];

  const providerLinks = [
    {
      path: '/provider-profile',
      label: 'My Provider Profile',
      icon: '👨‍🏫',
      requiresAuth: true
    },
    {
      path: '/provider-sessions',
      label: 'My Provider Sessions',
      icon: '📋',
      requiresAuth: true
    },
    {
      path: '/connect-practitioners',
      label: 'Connect with Practitioners',
      icon: '🤝',
      requiresAuth: true
    },
    {
      path: '/reviews-ratings',
      label: 'My Reviews and Ratings',
      icon: '⭐',
      requiresAuth: true
    },
    {
      path: '/earnings',
      label: 'Earnings & Payouts',
      icon: '💰',
      requiresAuth: true
    }
  ];

  const adminLinks = [
    {
      path: '/admin/dashboard',
      label: 'Admin Dashboard',
      icon: '🔧',
      requiresAuth: true
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      // Clear user profile state immediately
      setUserProfile(null);
      setCurrentRole('learner');
      // Navigate to home page after successful logout
      navigate('/');
      if (isMobile && closeMobileMenu) {
        closeMobileMenu();
      }
    } catch (error) {
      console.error('Failed to log out');
    }
  };

  const handleNavigation = (item) => {
    if (item.requiresAuth && !currentUser) {
      navigate('/login');
    } else {
      navigate(item.path);
    }
    
    // Close mobile menu after navigation
    if (isMobile && closeMobileMenu) {
      closeMobileMenu();
    }
  };

  const navigateAndClose = (path) => {
    navigate(path);
    if (isMobile && closeMobileMenu) {
      closeMobileMenu();
    }
  };

  const getRelevantLinks = () => {
    if (!userProfile) return practitionerLinks.filter(link => !link.requiresAuth);
    
    if (userProfile.userType === 'both') {
      // Show links based on current active role
      return currentRole === 'provider' ? providerLinks : practitionerLinks;
    } else if (userProfile.userType === 'provider') {
      // Providers should also see learner options
      return [...practitionerLinks, ...providerLinks];
    } else {
      return practitionerLinks;
    }
  };

  const getSectionTitle = () => {
    if (!userProfile) return '';
    
    if (userProfile.userType === 'both') {
      return currentRole === 'provider' ? 'Provider Dashboard' : 'Practitioner Dashboard';
    } else if (userProfile.userType === 'provider') {
      return 'Provider Dashboard';
    } else {
      return 'Practitioner Dashboard';
    }
  };

  return (
    <div className={`sidebar ${isMobile ? 'mobile' : ''} ${isMobileMenuOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2>Practice Talking</h2>
        {currentUser && userProfile && (
          <div className="user-welcome">
            <p>Welcome, {userProfile.firstName || userProfile.displayName?.split(' ')[0] || 'User'}</p>
            <RoleSwitcher />
          </div>
        )}
      </div>
      
      <nav className="sidebar-nav">
        {/* General Navigation - Always visible */}
        <div className="nav-section">
          {generalLinks.map(item => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Admin Links - Show first if user is admin */}
        {userProfile && userProfile.isAdmin && (
          <div className="nav-section">
            <h3 className="nav-section-title">Admin Panel</h3>
            {adminLinks.map(item => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item)}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Regular User Links */}
        {userProfile && userProfile.userType === 'provider' ? (
          <>
            <div className="nav-section">
              <h3 className="nav-section-title">Practitioner Links</h3>
              {practitionerLinks.map(item => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item)}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </div>
            <div className="nav-section">
              <h3 className="nav-section-title">Provider Links</h3>
              {providerLinks.map(item => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item)}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="nav-section">
            <h3 className="nav-section-title">{getSectionTitle()}</h3>
            {getRelevantLinks().map(item => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item)}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </nav>
      
      <div className="sidebar-footer">
        {currentUser && (
          <>
            <button
              onClick={() => navigateAndClose('/messages')}
              className={`nav-item ${location.pathname === '/messages' ? 'active' : ''}`}
            >
              <span className="nav-icon">💬</span>
              <span className="nav-label">Messages</span>
            </button>
            <button
              onClick={() => navigateAndClose('/account')}
              className={`nav-item ${location.pathname === '/account' ? 'active' : ''}`}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-label">My Account</span>
            </button>
            <button
              onClick={() => navigateAndClose('/help-support')}
              className={`nav-item ${location.pathname === '/help-support' ? 'active' : ''}`}
            >
              <span className="nav-icon">🆘</span>
              <span className="nav-label">Help & Support</span>
            </button>
          </>
        )}
        
        {/* Legal Links - Always visible */}
        <div className="legal-links">
          <button
            onClick={() => navigateAndClose('/provider-guidelines')}
            className={`nav-item legal-link ${location.pathname === '/provider-guidelines' ? 'active' : ''}`}
          >
            <span className="nav-icon">🏆</span>
            <span className="nav-label">How to Be a Great Provider</span>
          </button>
          <button
            onClick={() => navigateAndClose('/terms')}
            className={`nav-item legal-link ${location.pathname === '/terms' ? 'active' : ''}`}
          >
            <span className="nav-icon">📋</span>
            <span className="nav-label">Terms & Conditions</span>
          </button>
          <button
            onClick={() => navigateAndClose('/privacy')}
            className={`nav-item legal-link ${location.pathname === '/privacy' ? 'active' : ''}`}
          >
            <span className="nav-icon">🔒</span>
            <span className="nav-label">Privacy Policy</span>
          </button>
        </div>
        
        {currentUser ? (
          <button onClick={handleLogout} className="logout-btn">
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Logout</span>
          </button>
        ) : (
          <button onClick={() => navigateAndClose('/login')} className="login-btn">
            <span className="nav-icon">🔑</span>
            <span className="nav-label">Sign In</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;